import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Features from "@/components/Features";
import Footer from "@/components/Footer";
import MetaTags from "@/components/MetaTags";
import ScrollingUpdates from "@/components/ScrollingUpdates";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import {
  ArrowRight,
  BookOpen,
  Code,
  ExternalLink,
  FileText,
  Star,
  Clock,
  Tags,
  Check,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { articles } from "@/data/articles";
import { getArticleBySlugSync } from "@/utils/articleQueries";
import { shouldBypassContentProcessing } from "@/utils/productionFallbacks";
import ResourceCard from "@/components/ResourceCard";
import { usePageTracking } from "@/hooks/usePageTracking";
import { trackButtonClick } from "@/utils/tracking";
import { trackCustomEvent } from "@/utils/analyticsTracker";
import TestimonialsPanel from "@/components/premium/TestimonialsPanel";
import { toast } from "@/hooks/use-toast";

const Index = () => {
  // Use our page tracking hook
  usePageTracking();
  const { isSubscribed, user } = useAuth();

  // Explicitly mark the homepage and clean up any problematic localStorage items
  useEffect(() => {
    console.log(
      "Index: Homepage mounted - this route should bypass content processing",
    );

    // Set a flag in sessionStorage to indicate we're on the homepage
    sessionStorage.setItem("isHomepage", "true");

    // Clear any problematic content from localStorage including Vite asset references
    try {
      const keysToCheck = Object.keys(localStorage).filter(
        (key) =>
          key.includes("content") ||
          key.includes("article") ||
          key.includes("export"),
      );

      if (keysToCheck.length > 0) {
        console.log(
          "Index: Found potential content-related localStorage items, checking for Vite references",
        );

        keysToCheck.forEach((key) => {
          const value = localStorage.getItem(key);
          if (
            value &&
            (value.includes("export default") || value.includes("/assets/"))
          ) {
            console.log(
              `Index: Removing problematic localStorage item: ${key}`,
            );
            localStorage.removeItem(key);
          }
        });
      }

      // Also remove any sessionStorage items that might have the same issue
      const sessionKeys = Object.keys(sessionStorage).filter(
        (key) =>
          key.includes("content") ||
          key.includes("article") ||
          key.includes("export"),
      );

      sessionKeys.forEach((key) => {
        const value = sessionStorage.getItem(key);
        if (
          value &&
          (value.includes("export default") || value.includes("/assets/"))
        ) {
          console.log(
            `Index: Removing problematic sessionStorage item: ${key}`,
          );
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Index: Error cleaning storage", error);
    }

    return () => {
      // Clear the homepage flag when navigating away
      sessionStorage.removeItem("isHomepage");
    };
  }, []);

  // Show success announcement on first visit
  useEffect(() => {
    const key = "naren_offer_announcement";
    const existingValue = localStorage.getItem(key);
    
    console.log("Naren toast check:", {
      key,
      existingValue,
      shouldShow: !existingValue,
      timestamp: new Date().toISOString()
    });
    
    if (!existingValue) {
      console.log("Showing Naren toast...");
      toast({
        title: "Success Story",
        description: "Naren, one of our coached candidates, just accepted an offer!",
      });
      localStorage.setItem(key, "true");
      console.log("Naren toast shown and localStorage updated");
    } else {
      console.log("Naren toast already shown before, skipping");
    }
  }, []);

  // Track successful Stripe checkout returns
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      trackCustomEvent("stripe_checkout_success", {
        extraData: { sessionId: params.get("session_id") },
      });

      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Get the ads recommendation article for the Just Published section
  const inSessionArticle = getArticleBySlugSync("ads-ranking");

  // Create structured reviews for meta tags (keep these for SEO)
  const reviews = [
    {
      quote:
        "Thank you so much for the time you spent. I gained a lot of insights on what I should do to improve.",
      author: "Meta ML Engineer",
      title: "ML Engineer @ Meta",
    },
    {
      quote:
        "Coach shared how to structure ML Design interviews for all companies.",
      author: "Meta Candidate",
      title: "ML Candidate @ Meta",
    },
    {
      quote:
        "Professional and thorough ML system design interview. The coach asked relevant questions to assess understanding and provided specific, actionable feedback for improvement. Clear guidance on how to strengthen system design skills. Highly valuable session",
      author: "Uber ML Candidate",
      title: "ML Candidate @ Uber",
    },
    {
      quote:
        "Thanks for the great feedback! Immensely helpful discussion and I got a lot of value out of the session esp understanding the expectations around equations, trade-offs and prob formulation at L6/7 level.",
      author: "Senior Staff ML Engineer",
      title: "Senior Staff ML Engineer @ Pinterest, Meta, Stripe",
    },
    {
      quote:
        "Amazing coach. Very knowledgeable and gave relevant, level specific feedback!",
      author: "Meta E6 ML Candidate",
      title: "E6 ML Candidate @ Meta",
    },
  ];

  const structuredReviews = reviews.map((review) => ({
    author: review.author,
    reviewBody: review.quote,
    reviewRating: 5, // Since all testimonials are positive
  }));

  const features = [
    {
      icon: <BookOpen className="h-8 w-8 text-primary" />,
      title: "Comprehensive ML System Design Guide",
      description:
        "Access in-depth guides on designing scalable ML systems for production environments with best practices for model deployment, monitoring and maintenance.",
      link: "/learn", // Added link to /learn page
    },
    {
      icon: <FileText className="h-8 w-8 text-primary" />,
      title: "Real Interview Questions",
      description:
        "Practice with actual ML system design questions asked at FAANG companies, complete with detailed solutions and explanations.",
      link: "/coaching", // Added link to /coaching page
    },
    {
      icon: <Code className="h-8 w-8 text-primary" />,
      title: "Architecture Templates",
      description:
        "Use proven templates for common ML systems like recommendation engines, ranking systems, and computer vision pipelines.",
      link: "/learn", // Added link to /learn page
    },
  ];

  useEffect(() => {
    const observerCallback: IntersectionObserverCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add a slight delay based on the index for staggered animations
          const target = entry.target as HTMLElement;
          const delay = target.dataset.delay || "0";

          // Apply the animation after a delay
          setTimeout(() => {
            const animationClass =
              target.dataset.animation || "animate-fade-in";
            entry.target.classList.add(animationClass);
            entry.target.classList.remove("animate-hidden");
          }, parseInt(delay));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.2,
      rootMargin: "0px 0px -100px 0px",
    });

    document.querySelectorAll(".scroll-animate").forEach((element, index) => {
      const el = element as HTMLElement;
      // Set default animation class and add delay for staggered effect
      el.dataset.animation = el.dataset.animation || "animate-fade-in";
      el.dataset.delay = el.dataset.delay || (index * 150).toString();
      el.classList.add("animate-hidden");
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  // Track button clicks for the "Get Started" button
  const handleGetStartedClick = () => {
    trackButtonClick("homepage_get_started_button", {
      isSubscribed: isSubscribed,
      userEmail: user?.email,
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title="HelloML - Master ML System Design Interviews | FAANG Interview Preparation"
        description="Master ML system design interviews with HelloML. Expert-curated resources, coaching, and practice questions to help you ace machine learning interviews at FAANG companies."
        reviews={structuredReviews}
      />
      <Navbar />
      <ScrollingUpdates />

      <main className="flex-grow">
        <Hero />

        {/* Redesigned Just Published Section */}
        <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/10 border-y">
          <div className="container">
            <div
              className="text-center mb-8 scroll-animate"
              data-animation="animate-slide-up"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-2">
                Just Published
              </h2>
              <p className="text-xl text-muted-foreground">
                Check out our latest ML system design content
              </p>
            </div>

            {inSessionArticle && (
              <div
                className="grid md:grid-cols-3 gap-8 scroll-animate"
                data-animation="animate-slide-up"
              >
                <div className="md:col-span-2 space-y-4">
                  <div className="bg-card rounded-lg border p-6 h-full flex flex-col">
                    <div className="flex justify-between items-start mb-4">
                      <span className="bg-primary/10 text-primary text-sm px-3 py-1 rounded-full">
                        {inSessionArticle.category}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {inSessionArticle.publishDate}
                      </span>
                    </div>

                    <h3 className="text-2xl font-bold mb-3">
                      {inSessionArticle.title}
                    </h3>

                    <p className="text-muted-foreground mb-4 flex-grow">
                      {inSessionArticle.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {inSessionArticle.tags?.map((tag, i) => (
                        <span
                          key={i}
                          className="bg-accent/60 text-accent-foreground text-xs px-2 py-1 rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    <div className="flex items-center justify-between mt-auto">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Clock className="mr-1 h-4 w-4" />
                        <span>{inSessionArticle.readTime}</span>
                      </div>

                      {/* Replace direct Link with ResourceCard behavior */}
                      <ResourceCard
                        title={inSessionArticle.title}
                        description={inSessionArticle.description}
                        category={inSessionArticle.category}
                        isPremium={inSessionArticle.isPremium}
                        link={`/articles/${inSessionArticle.slug}`}
                        readTime={inSessionArticle.readTime}
                        readCount={inSessionArticle.readCount}
                        likeCount={inSessionArticle.likeCount}
                        comingSoon={inSessionArticle.comingSoon}
                        isPreviewEnabled={false}
                        renderAsButton={true}
                      />
                    </div>
                  </div>
                </div>

                <div className="md:col-span-1">
                  <div className="bg-card rounded-lg border p-6 h-full">
                    <h4 className="font-semibold mb-4 flex items-center">
                      <BookOpen className="h-4 w-4 mr-2" /> Why This Matters
                    </h4>
                    <p className="text-muted-foreground mb-4">
                      Ads ranking is the backbone of monetization for many
                      platforms. A well-designed system ensures relevant ads
                      while maximizing revenue.
                    </p>
                    <h4 className="font-semibold mb-4 flex items-center">
                      <Tags className="h-4 w-4 mr-2" /> Key Topics
                    </h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                        Real-time bidding integration
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                        Ad relevance optimization
                      </li>
                      <li className="flex items-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                        Campaign performance metrics
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 bg-background border-y">
          <div className="container">
            <div
              className="text-center mb-8 scroll-animate"
              data-animation="animate-slide-up"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-2">
                Testimonials
              </h2>
              <p className="text-xl text-muted-foreground">
                What our community says about us
              </p>
            </div>
            <div className="max-w-lg mx-auto">
              <TestimonialsPanel />
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-accent">
          <div className="container">
            <div
              className="text-center mb-12 scroll-animate"
              data-animation="animate-slide-up"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Premium Resources for ML Engineers
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Everything you need to excel in machine learning system design
                interviews and build robust ML systems.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, i) => (
                <Link to={feature.link} key={i} className="block">
                  <div
                    className="bg-card rounded-lg p-6 shadow-sm border scroll-animate hover:bg-accent/50 transition-colors"
                    data-animation="animate-slide-up"
                    data-delay={`${(i + 1) * 200}`}
                  >
                    <div className="mb-5">{feature.icon}</div>
                    <h3 className="text-xl font-medium mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24 bg-gradient text-white">
          <div
            className="container text-center scroll-animate"
            data-animation="animate-fade-in"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to master ML system design?
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto mb-8">
              Join HelloML today and get access to premium resources that will
              help you ace your next ML system design interview.
            </p>
            {isSubscribed ? (
              <Button
                size="lg"
                variant="secondary"
                className="text-base px-6"
                disabled
              >
                You're Already Subscribed <Check className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Link to="/get-premium" onClick={handleGetStartedClick}>
                <Button
                  size="lg"
                  variant="secondary"
                  className="text-base px-6"
                >
                  Get Started for $29/month
                </Button>
              </Link>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
